// URL utility functions for canonical URL management

export const CANONICAL_DOMAIN = 'https://tinyminds.in';

/**
 * Ensures all internal links use the canonical domain
 */
export const getCanonicalUrl = (path: string = ''): string => {
  // Remove leading slash if present, we'll add it
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  // Remove trailing slash except for root
  const finalPath = cleanPath === '' ? '' : `/${cleanPath.replace(/\/$/, '')}`;
  return `${CANONICAL_DOMAIN}${finalPath}`;
};

/**
 * Check if current URL is a Lovable preview URL
 */
export const isLovablePreview = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  const href = window.location.href;
  
  // More comprehensive Lovable preview detection
  return hostname === 'localhost' ||
         hostname === '127.0.0.1' ||
         hostname.includes('lovable.app') || 
         hostname.includes('lovable.dev') || 
         hostname.includes('preview--') ||
         href.includes('.lovable.app') ||
         href.includes('.lovable.dev');
};

/**
 * Check if current URL matches canonical format
 */
export const isCanonicalUrl = (): boolean => {
  if (typeof window === 'undefined') return true;
  
  // Skip canonicalization for Lovable preview URLs
  if (isLovablePreview()) return true;
  
  const currentUrl = window.location.href;
  const currentOrigin = window.location.origin;
  
  return currentOrigin === CANONICAL_DOMAIN.replace(/\/$/, '');
};

/**
 * Redirect to canonical URL if needed (client-side)
 */
export const redirectToCanonical = (): void => {
  if (typeof window === 'undefined') return;
  
  // Skip canonicalization for Lovable preview URLs
  if (isLovablePreview()) {
    console.log('[TinyMinds] Lovable preview detected, skipping canonical redirect');
    return;
  }
  
  try {
    const currentUrl = window.location.href;
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    
    // Check if we need to redirect
    if (!isCanonicalUrl() || currentPath.endsWith('/') && currentPath !== '/') {
      const canonicalUrl = getCanonicalUrl(window.location.pathname.replace(/\/$/, '')) + 
                           window.location.search + window.location.hash;
      
      // Only redirect if the canonical URL is different
      if (currentUrl !== canonicalUrl) {
        console.log('[TinyMinds] Redirecting to canonical URL:', canonicalUrl);
        window.location.replace(canonicalUrl);
      }
    }
  } catch (error) {
    console.error('[TinyMinds] Error in redirectToCanonical:', error);
  }
};

/**
 * Get structured data for current page
 */
export const getPageStructuredData = (pageType: string, title: string, description: string) => {
  const baseStructuredData = {
    "@context": "https://schema.org",
    "@type": pageType,
    "name": title,
    "description": description,
    "url": getCanonicalUrl(window.location?.pathname || ''),
    "publisher": {
      "@type": "Organization",
      "name": "TinyMinds",
      "logo": {
        "@type": "ImageObject",
        "url": `${CANONICAL_DOMAIN}/og-image.png`
      }
    },
    "inLanguage": "en-IN",
    "audience": {
      "@type": "Audience",
      "audienceType": "children ages 3-8 and parents"
    }
  };

  return baseStructuredData;
};